import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import type { WorkflowStatusId } from 'types/workflow/workflow-status';

/** אייקון וצבעי רקע כמו ברשימת שיחות / הגדרות התראות */
export type SystemNotificationVisual =
  | { kind: 'workflow'; status: WorkflowStatusId }
  | { kind: 'note' }
  | { kind: 'tag' };

export type SystemNotificationItem = {
  id: string;
  message: string;
  createdAt: number;
  /** When false/undefined, counts as unread for badge and styling */
  read?: boolean;
  visual?: SystemNotificationVisual;
  /** לשימוש בהתראות שירות (צבעי סכמה מהשרת) — אם אין `visual` */
  backgroundColor?: string;
  textColor?: string;
  /** true = הגיע מטבלת Updates בשרת (id בצורת srv-<n>); הידרציה מחליפה רק כאלה */
  fromServer?: boolean;
  /** נתיב ניווט בתוך האפליקציה — הופך את השורה ללחיצה (למשל /chats/972... או /broadcast-logs/123) */
  link?: string | null;
};

const MAX_ITEMS = 50;

type SystemNotificationsState = {
  items: SystemNotificationItem[];
};

//* הפעמון ניזון עכשיו מטבלת Updates בשרת (hydrate) — ה-mock seed הוסר.
const initialState: SystemNotificationsState = {
  items: [],
};

const systemNotificationsSlice = createSlice({
  name: 'systemNotifications',
  initialState,
  reducers: {
    pushSystemNotification(
      state,
      action: PayloadAction<{
        message: string;
        backgroundColor?: string;
        textColor?: string;
        id?: string;
        createdAt?: number;
        read?: boolean;
        visual?: SystemNotificationVisual;
        fromServer?: boolean;
        link?: string | null;
      }>,
    ) {
      const { message, backgroundColor, textColor, id, createdAt, read, visual, fromServer, link } = action.payload;
      const nid = id ?? `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      //* דחיפה חוזרת של אותו פריט שרת (סוקט + hydrate מקבילים) — לא מכפילים
      if (state.items.some((it) => it.id === nid)) return;
      state.items.unshift({
        id: nid,
        message,
        createdAt: createdAt ?? Date.now(),
        read: read ?? false,
        visual,
        backgroundColor,
        textColor,
        fromServer,
        link,
      });
      if (state.items.length > MAX_ITEMS) {
        state.items.length = MAX_ITEMS;
      }
    },
    markAllSystemNotificationsRead(state) {
      state.items.forEach((it) => {
        it.read = true;
      });
    },
    /** סימון פריט בודד כנקרא — בלחיצה על שורה לחיצה (עם link) */
    markSystemNotificationRead(state, action: PayloadAction<string>) {
      const item = state.items.find((it) => it.id === action.payload);
      if (item) item.read = true;
    },
    /**
     * הידרציה מהשרת (טבלת Updates): מחליפה את פריטי השרת בלבד ומשמרת פריטים
     * מקומיים (התראות שירות מהסוקט, שאינן נשמרות ב-DB). ממוינת חדש→ישן.
     */
    hydrateSystemNotifications(state, action: PayloadAction<SystemNotificationItem[]>) {
      const localItems = state.items.filter((it) => !it.fromServer);
      //* פריט מקומי שכבר קיים בשרת (תיוג שנדחף בסוקט ואז הגיע ב-hydrate) — מוסר לפי תוכן+דקה
      const serverKeys = new Set(
        action.payload.map((it) => `${it.message}|${Math.floor(it.createdAt / 60000)}`),
      );
      const dedupedLocal = localItems.filter(
        (it) => !serverKeys.has(`${it.message}|${Math.floor(it.createdAt / 60000)}`),
      );
      state.items = [...dedupedLocal, ...action.payload]
        .sort((a, b) => b.createdAt - a.createdAt)
        .slice(0, MAX_ITEMS);
    },
    clearSystemNotifications(state) {
      state.items = [];
    },
  },
});

export default systemNotificationsSlice.reducer;
export const {
  pushSystemNotification,
  markAllSystemNotificationsRead,
  markSystemNotificationRead,
  clearSystemNotifications,
  hydrateSystemNotifications,
} = systemNotificationsSlice.actions;
